import { useTheme } from "@amx/ux-twoui-theming";
import { Stack } from "@fluentui/react";
import React, { useContext, useEffect } from "react";
import { throwOrReportComponentValidationError, validateChildComponent } from "../../../shared/validation";
import { ProgressIndicator, ProgressIndicatorProps } from "../../ProgressIndicator/ProgressIndicator";
import { SlotStack, SlotStackItem } from "../../Stacks/SlotStack/SlotStack";
import { DrawerContext } from "../DrawerBase/DrawerBase";
import {
    getDrawerHeaderChevronSlotStyles,
    getDrawerHeaderChevronStyles,
    getDrawerHeaderColumnsStyles,
    getDrawerHeaderEndSlotStyles,
    getDrawerHeaderStartSlotStyles,
    getDrawerHeaderWrapperStyles
} from "./DrawerHeader.styles";
import { ChevronPosition, DrawerHeaderChevron } from "./DrawerHeaderChevron/DrawerHeaderChevron";
import { DrawerHeaderAction } from "./DrawerHeaderColumns/DrawerHeaderAction/DrawerHeaderAction";
import { DrawerHeaderCallout } from "./DrawerHeaderColumns/DrawerHeaderCallout/DrawerHeaderCallout";
import { DrawerHeaderError } from "./DrawerHeaderColumns/DrawerHeaderError/DrawerHeaderError";
import { DrawerHeaderLink } from "./DrawerHeaderColumns/DrawerHeaderLink/DrawerHeaderLink";
import { DrawerHeaderMedia } from "./DrawerHeaderColumns/DrawerHeaderMedia/DrawerHeaderMedia";
import { DrawerHeaderStatus } from "./DrawerHeaderColumns/DrawerHeaderStatus/DrawerHeaderStatus";
import { DrawerHeaderTitle } from "./DrawerHeaderColumns/DrawerHeaderTitle/DrawerHeaderTitle";
import { DrawerHeaderTitleWithShimmer } from "./DrawerHeaderColumns/DrawerHeaderTitle/DrawerHeaderTitleWithShimmer";
import { DrawerHeaderToggle } from "./DrawerHeaderColumns/DrawerHeaderToggle/DrawerHeaderToggle";

export const drawerHeaderComponentName = "DrawerHeader";

export interface DrawerHeaderColumn {
    /**
     * JSX element to be rendered within this column.
     */
    readonly column: JSX.Element;
    /**
     * Number of slots to allocate to this column.
     */
    readonly slots: number;
}

export type DrawerHeaderMinHeight = "48px" | "68px";

export interface DrawerHeaderProps {
    /**
     * Total number of slots used by this header.
     */
    readonly totalSlots: number;
    /**
     * Columns to render inside the drawer header.
     */
    readonly columns: readonly DrawerHeaderColumn[];
    /**
     * Chevron position inside the drawer header. Default value is to render at the start.
     */
    readonly chevronPosition?: ChevronPosition;
    /**
     * Whether we're displaying the progress indicator at the top of the drawer header.
     */
    readonly progressIndicator?: Pick<ProgressIndicatorProps, "ariaValueText">;
    /**
     * Optional boolean to change the background color of drawer. Defaults to false.
     */
    readonly showAltBackgroundColor?: boolean;
    /**
     * Optional min-height.
     */
    readonly minHeight?: DrawerHeaderMinHeight;
    /**
     * Optional padding in pixels to indent the header with. Default is "0px".
     */
    readonly indentPadding?: "0px" | "32px" | "64px";
}

// DrawerHeaderColumn valid components
export const validDrawerHeaderColumns: readonly React.ComponentType<any>[] = [
    DrawerHeaderTitle,
    DrawerHeaderStatus,
    DrawerHeaderAction,
    DrawerHeaderToggle,
    DrawerHeaderLink,
    DrawerHeaderError,
    DrawerHeaderMedia,
    DrawerHeaderTitleWithShimmer,
    DrawerHeaderCallout
];

export const DrawerHeader: React.FC<DrawerHeaderProps> = ({
    totalSlots,
    columns,
    chevronPosition = "start",
    progressIndicator,
    showAltBackgroundColor = false,
    minHeight = "48px",
    indentPadding = "0px"
}) => {
    const theme = useTheme();
    const acceptableFirstColumnTypes = [DrawerHeaderTitle, DrawerHeaderError, DrawerHeaderTitleWithShimmer, DrawerHeaderMedia];
    const acceptableFirstColumnTypesNames = acceptableFirstColumnTypes.map(type => type.name).join(", ");
    const drawerContext = useContext(DrawerContext);
    const drawerHeaderTitleId = drawerContext.drawerHeaderTitleId;

    const throwNotAcceptableFirstColumnTypeValidationError = () => {
        throwOrReportComponentValidationError("DrawerHeader", `Invalid first column for Drawer Header. Acceptable types for the DrawerHeader's first column are: ${acceptableFirstColumnTypesNames}`, 1);
    };

    let firstColumn: JSX.Element | undefined;
    let firstColumnWithId: JSX.Element | undefined;
    if (columns && columns.length >= 1) {
        firstColumn = columns[0].column;
    } else {
        firstColumn = undefined;
    }

    //Ensures we only run the validation once at first, and then when a column changes after the first render
    useEffect(() => {

        //Ensures that the first column exists and has a title
        if (!firstColumn || !(acceptableFirstColumnTypes.includes(firstColumn.type))) {
            throwNotAcceptableFirstColumnTypeValidationError();
        }

        columns.forEach((column) => {
            validateChildComponent(column.column, validDrawerHeaderColumns, drawerHeaderComponentName);
        });
    }, [columns]);

    const drawerHeaderChevron = (
        <Stack verticalAlign="center" styles={getDrawerHeaderChevronStyles()}>
            <DrawerHeaderChevron chevronPosition={chevronPosition} />
        </Stack>
    );

    // Ensures that firstColumn is not undefined - in such a case the rest of this return should behave the way it did before the addition of the id
    if (firstColumn) {
        firstColumnWithId = React.cloneElement(firstColumn, {
            id: drawerHeaderTitleId
        });
    }

    // Add the chevron only to the first or last element in the drawer depending on its position
    const slotStackColumns =
        columns.map((column, index) => {
            const isChevronSlot = (chevronPosition === "start" && index === 0) || (chevronPosition === "end" && index === columns.length - 1);
            const isEndSlot = (chevronPosition === "end" && index === 0) || (chevronPosition === "start" && index === columns.length - 1);

            return (
                <SlotStackItem slots={column.slots} key={index}>
                    <Stack horizontal={true} styles={isEndSlot ? getDrawerHeaderEndSlotStyles(chevronPosition, indentPadding) : index === 0 ? getDrawerHeaderStartSlotStyles(indentPadding) : undefined}>
                        {chevronPosition === "start" && index === 0 && drawerHeaderChevron}
                        <Stack styles={getDrawerHeaderChevronSlotStyles(isChevronSlot)}>
                            {(index === 0 && firstColumn) ? firstColumnWithId : column.column}
                        </Stack>
                        {chevronPosition === "end" && index === columns.length - 1 && drawerHeaderChevron}
                    </Stack>
                </SlotStackItem>
            );
        });

    const drawerHeaderProgressIndicator = progressIndicator
        ? <ProgressIndicator ariaValueText={progressIndicator?.ariaValueText} />
        : <></>;

    const headerPadding = progressIndicator ? "6px 0px 8px 0px" : "8px 0px";

    return (
        <Stack verticalAlign="center" styles={getDrawerHeaderWrapperStyles(theme, showAltBackgroundColor, minHeight)}>
            {drawerHeaderProgressIndicator}
            <Stack horizontal={true} verticalAlign="center" tokens={{ padding: headerPadding }} >
                <Stack verticalAlign="center" styles={getDrawerHeaderColumnsStyles()}>
                    <SlotStack verticalAlign="center" totalSlots={totalSlots}>
                        {slotStackColumns}
                    </SlotStack>
                </Stack>
            </Stack>
        </Stack>
    );
};
